import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { exportMarkupJpeg } from './exportMarkup';
import type { MarkupShape } from './markupShapes';

export type MarkupAttachState =
    | { status: 'idle' }
    | { status: 'working' }
    | { status: 'rejected' }
    | { status: 'failed'; message: string };

const EXPORT_ERROR = 'Could not prepare the image. Try again, or pick the photo again.';

// onAttach returns false when the composer rejects the file; it shows its own notice then.
export function useMarkupAttach(
    image: ImageBitmap | null,
    shapes: readonly MarkupShape[],
    onAttach: (jpeg: Blob) => boolean
): { state: MarkupAttachState; attach: () => Promise<void> } {
    const [state, setState] = useState<MarkupAttachState>({ status: 'idle' });

    const attach = useCallback(async () => {
        if (!image) return;
        setState({ status: 'working' });
        try {
            const jpeg = await exportMarkupJpeg(image, shapes);
            setState(onAttach(jpeg) ? { status: 'idle' } : { status: 'rejected' });
        } catch (error) {
            // A tainted canvas lands here; the error names the browser rule, never the photo.
            Sentry.captureException(error, { tags: { feature: 'dm-scan-markup' } });
            setState({ status: 'failed', message: EXPORT_ERROR });
        }
    }, [image, shapes, onAttach]);

    return { state, attach };
}
